/**
 * <s-reveal> — fades descendants marked `.reveal-on-scroll` into view as they
 * enter the viewport. Adds `is-visible` once per element. Without JS (or before
 * the element is defined) content stays fully visible; the hidden start state
 * is scoped to `s-reveal:defined` in the stylesheet.
 */
class SReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const targets = this.querySelectorAll('.reveal-on-scroll:not(.is-visible)');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          this.#observer?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );

    targets.forEach((el) => this.#observer?.observe(el));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('s-reveal')) customElements.define('s-reveal', SReveal);
